"use client";

import Link from "next/link";
import { useState, useRef, MouseEvent } from "react";
import { Github, Linkedin, Twitter, ArrowRight } from "lucide-react";
import { certifications } from "@/lib/data";
import { getCaseStudies } from "@/lib/content-engine";
// IMPORT THE SHARED COMPONENT
import { UniversalLogo } from "@/components/ui/UniversalLogo";

export default function Footer() {
  const rawStudies = getCaseStudies() || [];
  const clients = rawStudies.map(study => ({
    name: study.clientName,
    href: `/case-studies/${study.slug}`,
    logo: study.logo,
    role: study.role
  }));

  return (
    <footer className="bg-slate-900 text-white border-t border-slate-800">
      
      {/* SCROLLER 1: CLIENTS */}
      <div className="border-b border-slate-800 bg-slate-900/50 py-12 relative z-10">
        <div className="max-w-7xl mx-auto px-4 mb-8 text-center">
           <p className="text-primary uppercase tracking-widest text-xs font-bold">
            Trusted by Innovative Market Leaders
          </p>
        </div>

        <DraggableScroll className="mask-linear-fade">
          <div className="flex animate-infinite-scroll group-hover:[animation-play-state:paused] w-max">
            <div className="flex items-center shrink-0">
               {clients.map((client, i) => (
                 <ClientCard key={`c1-${i}`} client={client} />
               ))}
            </div>
            <div className="flex items-center shrink-0">
               {clients.map((client, i) => (
                 <ClientCard key={`c2-${i}`} client={client} />
               ))}
            </div>
          </div>
        </DraggableScroll>
      </div>

      {/* SCROLLER 2: PARTNERS */}
      <div className="border-b border-slate-800 bg-slate-950 py-10 relative z-0">
        <div className="max-w-7xl mx-auto px-4 mb-6 text-center">
           <p className="text-slate-500 uppercase tracking-widest text-[10px] font-bold">
            Partners & Certifications
          </p>
        </div>
        
        <DraggableScroll className="mask-linear-fade">
          <div className="flex animate-infinite-scroll group-hover:[animation-play-state:paused] w-max">
            <div className="flex items-center shrink-0">
               {certifications.map((cert, i) => (
                  <PartnerLogo key={`p1-${i}`} cert={cert} />
               ))}
            </div>
            <div className="flex items-center shrink-0">
               {certifications.map((cert, i) => (
                  <PartnerLogo key={`p2-${i}`} cert={cert} />
               ))}
            </div>
          </div>
        </DraggableScroll>
      </div>

      {/* FOOTER LINKS */}
      <div className="max-w-7xl mx-auto px-4 py-16 grid md:grid-cols-4 gap-12">
        <div className="col-span-2">
          <Link href="/" className="text-2xl font-bold tracking-tighter text-white">
            AllCode<span className="text-primary">.</span>
          </Link>
          <p className="mt-4 text-slate-400 max-w-sm">
            We build scalable cloud engines for enterprise, fintech, and healthcare.
          </p>
          <div className="flex gap-4 mt-6">
            <Link href="#" className="text-slate-400 hover:text-white"><Github className="h-5 w-5" /></Link>
            <Link href="#" className="text-slate-400 hover:text-white"><Linkedin className="h-5 w-5" /></Link>
            <Link href="#" className="text-slate-400 hover:text-white"><Twitter className="h-5 w-5" /></Link>
          </div>
        </div>
        
        <div>
          <h4 className="font-bold mb-6">Services</h4>
          <ul className="space-y-4 text-slate-400">
            <li><Link href="/services/aws" className="hover:text-primary">AWS &Cloud</Link></li>
            <li><Link href="/services/gen-ai" className="hover:text-primary">Gen AI & Data</Link></li>
            <li><Link href="/services/software" className="hover:text-primary">Software Solutions</Link></li>
            <li><Link href="/case-studies" className="hover:text-primary">Case Studies</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="font-bold mb-6">Company</h4>
          <ul className="space-y-4 text-slate-400">
            <li><Link href="/company" className="hover:text-primary">About Us</Link></li>
            <li><Link href="/articles" className="hover:text-primary">Articles</Link></li>
            <li><Link href="/company" className="hover:text-primary">Careers</Link></li>
            <li><Link href="/contact" className="hover:text-primary">Contact</Link></li>
          </ul>
        </div>
      </div>

      <div className="border-t border-slate-800 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 py-6 flex flex-col md:flex-row justify-between items-center text-sm text-slate-500">
          <p>&copy; {new Date().getFullYear()} AllCode Inc. All rights reserved.</p>
          <div className="flex gap-8 mt-4 md:mt-0">
            <Link href="#" className="hover:text-white">Privacy Policy</Link>
            <Link href="#" className="hover:text-white">Terms of Service</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

// --- DRAGGABLE SCROLL COMPONENT ---
function DraggableScroll({ children, className }: { children: React.ReactNode, className?: string }) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const onMouseDown = (e: MouseEvent) => {
    if (!scrollRef.current) return;
    setIsDragging(true);
    setStartX(e.pageX - scrollRef.current.offsetLeft);
    setScrollLeft(scrollRef.current.scrollLeft);
  };

  const onMouseLeave = () => setIsDragging(false);
  const onMouseUp = () => setIsDragging(false);
  const onMouseMove = (e: MouseEvent) => {
    if (!isDragging || !scrollRef.current) return;
    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 2; 
    scrollRef.current.scrollLeft = scrollLeft - walk;
  };

  return (
    <div 
      ref={scrollRef}
      className={`overflow-hidden cursor-grab active:cursor-grabbing group ${className}`}
      onMouseDown={onMouseDown}
      onMouseLeave={onMouseLeave}
      onMouseUp={onMouseUp}
      onMouseMove={onMouseMove}
    >
      {children}
    </div>
  );
}

// --- CLIENT CARD ---
function ClientCard({ client }: { client: any }) {
  return (
    <Link href={client.href || '#'} className="shrink-0 w-48 mx-6 flex flex-col items-center group/item select-none" draggable={false}>
       <div className="h-24 w-48 bg-white rounded-lg flex flex-col items-center justify-center p-3 border-2 border-transparent group-hover/item:border-primary transition-all shadow-sm relative overflow-hidden gap-1">
          <div className="relative z-10 w-full h-10 flex items-center justify-center">
             {/* Using Shared Component */}
             <UniversalLogo 
               src={client.logo} 
               alt={client.name} 
               className="max-h-full max-w-full object-contain transition-all duration-300 transform group-hover/item:scale-105"
             />
          </div>
          <span className="text-slate-500 font-bold text-[10px] uppercase tracking-wider text-center leading-tight">
            {client.name}
          </span>
       </div>
       <div className="mt-3 flex items-center justify-center gap-2 text-[10px] font-bold text-slate-500 group-hover/item:text-primary uppercase tracking-wide transition-colors">
          View Work <ArrowRight className="h-3 w-3" />
       </div>
    </Link>
  );
}

// --- PARTNER LOGO ---
function PartnerLogo({ cert }: { cert: any }) {
  return (
    <div className="shrink-0 w-32 mr-16 flex flex-col items-center justify-center h-16 gap-2 group/partner select-none">
       {cert.logo && (
         <div className="h-10 w-full flex items-center justify-center bg-white rounded px-2 py-1 shadow-sm">
           {/* Using Shared Component */}
           <UniversalLogo 
             src={cert.logo} 
             alt={cert.name} 
             brightnessFix={false}
             className="max-h-full max-w-full object-contain"
           />
         </div>
       )}
       <span className="text-slate-500 font-mono text-[9px] text-center uppercase tracking-wider leading-none">
         {cert.name}
       </span>
    </div>
  );
}